import React, { useState, useEffect, memo } from "react";
import CacheBuster from "./Components/CacheBuster";
import { HashRouter as Router, Switch, Route } from "react-router-dom";
import Bookmark from "./Components/Bookmark";
import Restaurant from "./Components/Restaurant";
import Menus from "./Components/Menus";
import StoreProvider from "./Stores";
import { Spinner } from "react-bootstrap";
import { checkingWCAGConformance } from "./Services/FormatMenu";

const AppFresh = memo(() => {
  const [generalInfoData, setGeneralInfoData] = useState({
    menusData: { menus: [], menusPosition: [] },
    restaurantData: {
      name: "",
      introduction: "",
      displayDate: false,
      logoUrl: "",
      publishedDate: "",
      backgroundImageUrl: "",
      restaurantNameStyle: {},
      descriptionTextStyle: {},
      dateTextStyle: {},
    },
    isLoading: false,
    displayWCAGLogo: false,
    bookmarksEnabled: true,
    loadSectionOnly: false
  });

  useEffect(() => {
    const loadGeneralJSONData = () =>
      new Promise((resolve, reject) => {
        try {
          // Load menu data
          fetch("generalInfo.json?nocache=" + new Date().getTime())
            .then((r) => r.json())
            .then((data) => {
              return resolve({
                ...data,
                displayWCAGLogo: checkingWCAGConformance(data),
              });
            })
            .catch((err) => {
              console.log(err);
              reject("File parsing error, no rows found");
            });
        } catch (e) {
          reject("File parsing error, no rows found");
        }
      });

    const loadGeneralInfo = async () => {
      setGeneralInfoData((prevState) => ({ ...prevState, isLoading: true }));
      const response = await loadGeneralJSONData();

      const {
        name,
        introduction,
        displayDate,
        logoUrl,
        publishedDate,
        backgroundImageUrl,
        restaurantName,
        descriptionText,
        dateText,
        menus,
        menusPosition,
        displayCurrencySymbol,
        displayWCAGLogo,
        loadSectionOnly
      } = response;
      
      //By default bookmark should be enabled. This cover existing menus scenario.
      const bookmarksEnabled =
        response.bookmarksEnabled === undefined
          ? true
          : response.bookmarksEnabled;

      document.title = name + " - Digital Menu";

      setGeneralInfoData({
        menusData: {
          menus: menus ? menus : null,
          menusPosition: menusPosition,
          currencySymbol: displayCurrencySymbol ? "$" : "",
        },
        restaurantData: {
          name: name,
          introduction: introduction,
          displayDate: displayDate,
          logoUrl: logoUrl,
          publishedDate: new Date(publishedDate).toLocaleDateString(undefined, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          backgroundImageUrl: backgroundImageUrl,
          restaurantNameStyle: restaurantName,
          descriptionTextStyle: descriptionText,
          dateTextStyle: dateText,
        },
        isLoading: false,
        displayWCAGLogo,
        bookmarksEnabled,
        loadSectionOnly
      });
    };

    loadGeneralInfo();
  }, []);

  return (
    <StoreProvider>
      {generalInfoData.isLoading ? (
        <div className="spinner-backdrop d-flex justify-content-center align-items-center">
          <Spinner
            as="div"
            animation="border"
            size="lg"
            role="isProcessing"
            aria-hidden="true"
          />
        </div>
      ) : (
        <>
          <Restaurant restaurantData={generalInfoData.restaurantData} />
          <Router>
            <Switch>
              <Route exact path="/bookmark">
                <Bookmark menus={generalInfoData.menusData.menus} />
              </Route>
              <Route exact path={["/", "/:menuId", "/:menuId/:menuItemIndex"]}>
                <Menus
                  menusData={generalInfoData.menusData}
                  displayWCAGLogo={generalInfoData.displayWCAGLogo}
                  bookmarksEnabled={generalInfoData.bookmarksEnabled}
                  loadSectionOnly={generalInfoData.loadSectionOnly}
                />
              </Route>
            </Switch>
          </Router>
        </>
      )}
    </StoreProvider>
  );
});

const App = () => {
  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }) => {
        if (loading) return null;
        if (!loading && !isLatestVersion) refreshCacheAndReload();
        return <AppFresh />;
      }}
    </CacheBuster>
  );
};

export default App;
